import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';

export enum RecurrenceEditMode {
  THIS_EVENT = 'this',
  FOLLOWING_EVENTS = 'following',
}

interface EditRecurringEventConfirmationDialogProps {
  /**
   * Controls whether the dialog is open
   */
  open: boolean;
  /**
   * Callback function to close the dialog
   */
  onClose: () => void;
  /**
   * Callback function when user confirms their selection
   * @param mode - The selected recurrence edit mode
   */
  onConfirm: (mode: RecurrenceEditMode) => void;
  /**
   * Custom title for the dialog
   */
  title?: string;
  /**
   * Text for the confirm button
   */
  confirmButtonText?: string;
  /**
   * Mode indicating whether this is for edit or delete operation
   */
  mode?: 'edit' | 'delete';
}

/**
 * A dialog that prompts users when editing a recurring event,
 * allowing them to choose whether to edit just this event or this and following events.
 */
const EditRecurringEventConfirmationDialog: React.FC<EditRecurringEventConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Edit Recurring Event',
  confirmButtonText = 'OK',
  mode = 'edit',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMode, setSelectedMode] = useState<RecurrenceEditMode>(
    RecurrenceEditMode.THIS_EVENT,
  );

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedMode(event.target.value as RecurrenceEditMode);
  }, []);

  const handleClose = useCallback(() => {
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    await onConfirm(selectedMode);
    handleClose();
  }, [selectedMode, onConfirm, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-recurring-event-dialog-title"
      fullWidth
     maxWidth={"sm"}>
      <DialogTitle id="edit-recurring-event-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <RadioGroup
            aria-label="edit recurring event options"
            name="edit-recurring-event-options"
            value={selectedMode}
            onChange={handleChange}
          >
            <FormControlLabel
              value={RecurrenceEditMode.THIS_EVENT}
              control={<Radio />}
              label="This event"
            />
            <FormControlLabel
              value={RecurrenceEditMode.FOLLOWING_EVENTS}
              control={<Radio />}
              label="This and following events"
            />
          </RadioGroup>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={mode === 'delete' ? 'error' : 'primary'}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRecurringEventConfirmationDialog;
